import { translate } from "@jsverse/transloco";
import { NzUploadFile } from "ng-zorro-antd/upload";
import { apiErrorMessage } from "./api-error.helper";

export { fileTypeNotAllowedMessage } from "./api-error.helper";

export const IMAGE_MIME_TYPES = [
	"image/png",
	"image/jpeg",
	"image/gif",
	"image/bmp",
];

export const PDF_MIME_TYPES = ["application/pdf"];

/** Extensions a file name may carry for each accepted MIME type. */
const EXTENSIONS_BY_MIME_TYPE: Record<string, string[]> = {
	"image/png": [".png"],
	"image/jpeg": [".jpg", ".jpeg"],
	"image/gif": [".gif"],
	"image/bmp": [".bmp"],
	"application/pdf": [".pdf"],
};

const genericUploadErrorMessage = (): string =>
	translate("upload.errors.generic");

/** A picked file as the browser and the upload component describe it. */
export interface UploadCandidate {
	name?: string;
	type?: string;
}

const extensionsFor = (type: string): string[] =>
	EXTENSIONS_BY_MIME_TYPE[type] ?? [];

/**
 * Value for the file picker's `accept` attribute: the MIME types and their
 * extensions, so a file the OS reports without a type is still selectable.
 */
export const acceptFor = (allowedTypes: string[]): string =>
	[...allowedTypes, ...allowedTypes.flatMap(extensionsFor)].join(",");

/**
 * MIME type to upload the file as: the one the browser reports or, when it
 * reports none (usual for .pdf on Windows without a viewer associated), the
 * allowed type whose extension matches the name. Undefined when neither fits.
 */
export const resolveMimeType = (
	file: UploadCandidate,
	allowedTypes: string[],
): string | undefined => {
	if (file.type) return file.type;

	const name = file.name?.toLowerCase() ?? "";
	return allowedTypes.find((type) =>
		extensionsFor(type).some((extension) => name.endsWith(extension)),
	);
};

/**
 * True when the file may be uploaded, judged by its resolved MIME type. The
 * server validates the real content anyway, this only spares a round trip.
 */
export const isAllowedFileType = (
	file: UploadCandidate,
	allowedTypes: string[],
): boolean => {
	const type = resolveMimeType(file, allowedTypes);
	return !!type && allowedTypes.includes(type);
};

/**
 * Message to show for a failed upload: the server answers with a known error
 * code when the content is not a valid image/PDF; anything else gets the
 * generic text.
 */
export const uploadErrorMessage = (error: unknown): string =>
	apiErrorMessage(error, genericUploadErrorMessage());

export const pickedFile = (file: NzUploadFile): File => file as unknown as File;

export const toUploadFile = (file: NzUploadFile): NzUploadFile => {
	const picked = pickedFile(file);
	return {
		uid: file.uid,
		name: picked.name,
		size: picked.size,
		type: picked.type,
		status: "done",
		originFileObj: picked,
	};
};

const STORAGE_KEY_PREFIX =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;

export const displayFileName = (fileName: string): string =>
	(fileName.split("/").pop() ?? "").replace(STORAGE_KEY_PREFIX, "");
